/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { AdaptiveDialog } from '@/components/blocks/AdaptiveDialog';
import { Linkable } from '@/components/blocks/Linkable';
import { GuiaTallasDrawer } from '@/components/blocks/ProductDetails/GuiaTallasDrawer';
import { useStock } from '@/components/blocks/ProductDetails/hooks/useStock';
import { ProductToExhaustTextSx } from '@/components/blocks/ProductDetails/styles/availability';
import { ProductAttributeV2 } from '@/components/content/ProductDetails/parts/AttributeV2';
import {
	BoldTextAttrSx,
	noMargin,
	tileDescriptionSX,
	titleSelectSizeSx,
	valueSelectSizeSX,
} from '@/components/content/ProductDetails/styles/attributes';
import { getAttrsByIdentifier, useProductDetails } from '@/data/Content/ProductDetails';
import { ContentContext } from '@/data/context/content';
import { ProductAttributeValue } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TimerIcon from '@mui/icons-material/Timer';
import { Box, Fade, Skeleton, Stack, Typography } from '@mui/material';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { FC, Fragment, useContext, useState } from 'react';
const EMPTY_AVAILABILITY: ProductAvailabilityData[] = [];

export const ProductDetailsAvailability: FC = () => {
	const { availability = EMPTY_AVAILABILITY, selection } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	>;

	const availableBuyable = selection?.buyable;

	const availableQuantity = availability.length ? Number(availability[0].availableQuantity) : 0;

	if (availableQuantity > 15 || availableQuantity === 0 || !availableBuyable) {
		return <Stack sx={{ height: '24px' }}></Stack>;
	}

	return (
		<Fade in={true} timeout={1000}>
			<Typography sx={ProductToExhaustTextSx}>
				<TimerIcon
					sx={{ color: 'inherit', fontSize: 'var(--font-size-small)', marginRight: 0.5, my: '6px' }}
				/>
				{'Quedan pocas piezas, haz tu pedido pronto.'}
			</Typography>
		</Fade>
	);
};

interface PropsProductDetail {
	isDrawer?: boolean;
}

// eslint-disable-next-line complexity
export const ProductDetailsAttributes: FC<PropsProductDetail> = ({ isDrawer }) => {
	const { selection, product, onAttributeChange, availability, isLoadingInventory } = useContext(
		ContentContext
	) as ReturnType<typeof useProductDetails>;
	const [stock, setStock] = useState(true);
	const numAttrs = product?.definingAttributes?.length ?? 0;
	const skusAsAttrsList =
		product?.items?.map((sku) => getAttrsByIdentifier(sku.definingAttributes)) ?? [];

	const productAttrTallaDp = product?.attributes?.find(
		(attr) => attr.identifier === 'attr-talla-dp'
	);

	const { someWstock } = useStock(availability);
	if (productAttrTallaDp) {
		product?.definingAttributes?.map((attribute) => {
			if (attribute.identifier === 'Talla') {
				const dataFilterAttr: ProductAttributeValue[] = [];
				productAttrTallaDp.values.forEach((value) => {
					const findAttr = attribute?.values.find((elem) => elem.identifier === value.identifier);

					if (findAttr) {
						dataFilterAttr.push(findAttr);
					}
				});
				attribute.values = dataFilterAttr.length ? dataFilterAttr : attribute.values;
			}
		});
	}

	const tallaSeleccionada = selection?.attrsByIdentifier?.Talla;

	const openDrawerSizeGuide = useIsOpen();
	const handleIsStock = (stock: boolean) => {
		setStock(stock);
	};
	return (
		<Box id="product-details-sizes">
			{stock ? (
				<Stack
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						height: '25px',
					}}
				>
					<Stack sx={{ marginTop: '0px !important' }}>
						<Typography sx={titleSelectSizeSx}>
							{isDrawer ? 'Talla' : 'Selecciona talla: '}
							<Typography sx={valueSelectSizeSX} component="span">
								{tallaSeleccionada?.toUpperCase()}
							</Typography>
						</Typography>
					</Stack>

					{isDrawer ? (
						<Linkable href="" type="inline" sx={noMargin} onClick={openDrawerSizeGuide.on}>
							{'Guía de tallas'}
						</Linkable>
					) : null}

					<AdaptiveDialog
						open={openDrawerSizeGuide.open}
						onClose={openDrawerSizeGuide.off}
						title="GUIA DE TALLAS"
					>
						<GuiaTallasDrawer />
					</AdaptiveDialog>
				</Stack>
			) : null}

			{product?.definingAttributes?.map((attribute, index) => (
				<ProductAttributeV2
					key={attribute.identifier}
					attribute={attribute}
					skusAsAttrsList={skusAsAttrsList}
					onAttributeChange={onAttributeChange}
					selection={selection}
					isPivot={!!(index === 0 && numAttrs > 1)}
					isDrawer={isDrawer}
					hasStock={handleIsStock}
				/>
			))}
			{!isDrawer ? (
				<Fragment>
					{isLoadingInventory ? (
						<Skeleton
							variant="rectangular"
							height={60}
							sx={{
								width: '100%',
								marginTop: '7.72px !important',
								marginBottom: { xs: '0px !important', sm: '0px !important' },
							}}
						/>
					) : null}
					{someWstock && !isLoadingInventory ? (
						<Stack
							sx={{
								marginTop: '7.72px !important',
								marginBottom: { xs: '0px !important', sm: '0px !important' },
							}}
							direction={'row'}
						>
							<InfoOutlinedIcon
								sx={{
									color: '#565656',
									fontSize: 'var(--font-size-caption)',
									marginRight: '2px',
									marginTop: '4px',
								}}
							/>
							<Typography>
								<Typography sx={BoldTextAttrSx}>{'Las tallas son MX'}</Typography>
								<Typography sx={tileDescriptionSX}>
									{'(cm), consulta la'}
									<Linkable href="" type="inline" sx={noMargin} onClick={openDrawerSizeGuide.on}>
										{'Guía de tallas'}
									</Linkable>
									{' y compara tallas nacionales'}
								</Typography>
							</Typography>
						</Stack>
					) : null}
				</Fragment>
			) : null}
		</Box>
	);
};
